import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X, FileText, Send, Clock, User, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { INCIDENT_DESK_ROLE_IDS } from "../../utils/roles";
import { createPackageLog, getPackageLogs, getPackageIncidents } from "../../api/packages";
import { showToast } from "../../utils/showToast";
import moment from "moment";
import Spinner from "../spinner/spinner";
import {
  INCIDENT_SEVERITY,
  INCIDENT_RESPONSIBLE,
  INCIDENT_TYPE_LABEL,
  INCIDENT_STATUS,
  INCIDENT_STATUS_COLOR,
} from "../../constants/incidents";
import { IncidentDetailModal } from "../incidentDetail";
import IncidentForm from "../incidents/IncidentForm";
import "./PackageLogbook.scss";

// NOTE: PackageLogbook es un sidebar (drawer lateral), no un modal centrado.
// Candidato natural a migrar a ui/Drawer en un pase posterior; ui/Modal sería
// el primitive equivocado aquí porque añadiría posicionamiento centrado y
// rounded-xl sobre el panel.
// Tipos y subtipos hardcodeados
const LOG_TYPES = {
  contacto: {
    label: "Contacto",
    subtypes: {
      llamada: "Llamada telefónica",
      email: "Correo electrónico",
      mensaje: "Mensaje",
      visita: "Visita presencial",
    }
  },
  problema: {
    label: "Problema",
    subtypes: {
      direccion: "Problema con dirección",
      entrega: "Problema con entrega",
      producto: "Problema con producto",
      cliente: "Problema con cliente",
      otro: "Otro problema",
    }
  },
  seguimiento: {
    label: "Seguimiento",
    subtypes: {
      estado: "Cambio de estado",
      ubicacion: "Actualización de ubicación",
      reprogramacion: "Reprogramación",
      confirmacion: "Confirmación",
    }
  },
  resolucion: {
    label: "Resolución",
    subtypes: {
      solucionado: "Problema solucionado",
      reembolso: "Reembolso procesado",
      reenvio: "Reenvío programado",
      cancelacion: "Cancelación",
    }
  },
  informacion: {
    label: "Información",
    subtypes: {
      actualizacion: "Actualización de datos",
      consulta: "Consulta general",
      recordatorio: "Recordatorio",
      notificacion: "Notificación",
    }
  },
};

// Bitácoras que escribe microservice-packages (nadie las crea a mano): solo dan la
// etiqueta del historial. Van aparte de LOG_TYPES porque ese mapa alimenta el
// select del alta manual, y una bitácora manual con ese tipo se confundiría con una
// del sistema.
const SYSTEM_LOG_LABELS = {
  route_removal: {
    label: "Quitado de la ruta",
    subtypes: { driver: "Por el repartidor" },
  },
  return_completed: {
    label: "Devolución",
    subtypes: { success: "Completada" },
  },
  oversized: {
    label: "Sobredimensionado",
    subtypes: { weight: "Por peso", size: "Por tamaño" },
  },
};

// `incidentOrigin` y `onIncidentCreated` (opcionales) llegan desde EvidenceView y
// van al formulario de incidencias: el origen viaja en el alta y el callback
// avisa al contenedor después de recargar la lista propia. Sin ellos, igual que siempre.
export default function PackageLogbook({ packageId, isOpen, onClose, incidentOrigin, onIncidentCreated }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Quién ve la Auditoría (bitácora e incidencias del pedido): los 4 roles de la
  // mesa de incidencias (admin, superadmin, support y supervisor), por decisión
  // de producto. No usa canManageIncidents porque ese helper suma al
  // superadmin del sistema y la decisión no lo incluye. Es solo UX: el backend
  // aplica el permiso (IncidentStaffGuard).
  const canAudit = INCIDENT_DESK_ROLE_IDS.includes(user?.role);
  const [activeTab, setActiveTab] = useState("logbook"); // "logbook" o "incident"
  
  // Estados para bitácora
  const [selectedType, setSelectedType] = useState("");
  const [selectedSubtype, setSelectedSubtype] = useState("");
  const [comment, setComment] = useState("");

  // Modal de detalle
  const [selectedIncidentId, setSelectedIncidentId] = useState(null);
  
  const [logs, setLogs] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState(new Set());
  const [expandedIncidents, setExpandedIncidents] = useState(new Set());
  const sidebarRef = useRef(null);
  const formRef = useRef(null);

  // Cargar bitácoras e incidencias al abrir
  useEffect(() => {
    if (isOpen && packageId && canAudit) {
      loadLogs();
      loadIncidents();
    }
  // loadLogs y loadIncidents se recrean en cada render: como dependencias
  // dispararían la carga en cada render. Depende solo de abrir, el paquete y el permiso.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, packageId, canAudit]);

  // Cerrar con ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Bloquear scroll del body cuando está abierto
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const loadLogs = async () => {
    if (!packageId) return;
    
    setLoading(true);
    try {
      const response = await getPackageLogs(packageId);
      if (response.success && response.data) {
        setLogs(response.data);
      }
    } catch (error) {
      console.error("Error loading logs:", error);
      showToast(t("Error al cargar las bitácoras"), false);
    } finally {
      setLoading(false);
    }
  };

  const loadIncidents = async () => {
    if (!packageId) return;
    
    try {
      const response = await getPackageIncidents(packageId);
      if (response.success && response.data) {
        setIncidents(response.data);
      }
    } catch (error) {
      console.error("Error loading incidents:", error);
      showToast(t("Error al cargar las incidencias"), false);
    }
  };

  const handleTypeChange = (e) => {
    const type = e.target.value;
    setSelectedType(type);
    setSelectedSubtype(""); // Resetear subtipo cuando cambia el tipo
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedType || !selectedSubtype || !comment.trim()) {
      showToast(t("Por favor completa todos los campos"), false);
      return;
    }

    setSubmitting(true);
    try {
      const response = await createPackageLog({
        packageId,
        type: selectedType,
        subtype: selectedSubtype,
        comment: comment.trim(),
      });

      if (response.success) {
        showToast(t("Bitácora agregada correctamente"), true);
        setSelectedType("");
        setSelectedSubtype("");
        setComment("");
        loadLogs(); // Recargar bitácoras
      } else {
        showToast(response.error?.message || t("Error al guardar la bitácora"), false);
      }
    } catch (error) {
      console.error("Error submitting log:", error);
      showToast(t("Error al guardar la bitácora"), false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleIncidentCreated = (incident) => {
    loadIncidents();
    onIncidentCreated?.(incident);
  };

  const handleViewIncident = (incidentId) => {
    setSelectedIncidentId(incidentId);
  };

  const handleIncidentModalClose = () => {
    setSelectedIncidentId(null);
    loadIncidents();
  };

  const toggleLogExpansion = (logId) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const toggleIncidentExpansion = (incidentId) => {
    setExpandedIncidents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(incidentId)) {
        newSet.delete(incidentId);
      } else {
        newSet.add(incidentId);
      }
      return newSet;
    });
  };

  const getTypeLabel = (type) => {
    return LOG_TYPES[type]?.label || SYSTEM_LOG_LABELS[type]?.label || type;
  };

  const getSubtypeLabel = (type, subtype) => {
    return (
      LOG_TYPES[type]?.subtypes?.[subtype] ||
      SYSTEM_LOG_LABELS[type]?.subtypes?.[subtype] ||
      subtype
    );
  };

  const availableSubtypes = selectedType ? Object.entries(LOG_TYPES[selectedType]?.subtypes || {}) : [];

  // Función para obtener el orden de prioridad de una incidencia
  const getIncidentPriority = (severity) => {
    const priorityMap = {
      critica: 1,
      alta: 2,
      media: 3,
      baja: 4,
    };
    return priorityMap[severity] || 5;
  };

  // Ordenar incidencias por prioridad (Crítica > Alta > Media > Baja)
  const sortedIncidents = [...incidents].sort((a, b) => {
    const priorityA = getIncidentPriority(a.severity);
    const priorityB = getIncidentPriority(b.severity);
    return priorityA - priorityB;
  });

  if (!canAudit) {
    return null;
  }

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="package-logbook-overlay" 
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div 
        ref={sidebarRef}
        className={`package-logbook-sidebar ${isOpen ? 'package-logbook-sidebar--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="logbook-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="package-logbook-header">
          <div className="package-logbook-header__title">
            <FileText size={20} />
            <h2 id="logbook-title">{t("Bitácora del pedido")}</h2>
          </div>
          <button
            className="package-logbook-header__close"
            onClick={onClose}
            aria-label={t("Cerrar bitácora")}
          >
            <X size={20} />
          </button>
        </div>

        <div className="package-logbook-content">
          {/* Tabs para seleccionar entre bitácora e incidencia */}
          <div className="package-logbook-tabs">
            <button
              type="button"
              className={`package-logbook-tab ${activeTab === "logbook" ? "package-logbook-tab--active" : ""}`}
              onClick={() => setActiveTab("logbook")}
            >
              <FileText size={18} />
              <span>{t("Nueva bitácora")}</span>
            </button>
            <button
              type="button"
              className={`package-logbook-tab ${activeTab === "incident" ? "package-logbook-tab--active" : ""}`}
              onClick={() => setActiveTab("incident")}
            >
              <AlertTriangle size={18} />
              <span>{t("Abrir incidencia")}</span>
            </button>
          </div>

          {/* Formulario para nueva bitácora */}
          {activeTab === "logbook" && (
            <form ref={formRef} onSubmit={handleSubmit} className="package-logbook-form">
              <div className="package-logbook-form__section">
                <h3 className="package-logbook-form__section-title">{t("Nueva bitácora")}</h3>
              
              {/* Tipo */}
              <div className="package-logbook-form__field">
                <label className="package-logbook-form__label">
                  {t("Tipo")} <span className="required">*</span>
                </label>
                <select
                  className="package-logbook-form__select"
                  value={selectedType}
                  onChange={handleTypeChange}
                  required
                >
                  <option value="">{t("Selecciona un tipo")}</option>
                  {Object.entries(LOG_TYPES).map(([key, type]) => (
                    <option key={key} value={key}>
                      {t(type.label)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subtipo */}
              {selectedType && (
                <div className="package-logbook-form__field">
                  <label className="package-logbook-form__label">
                    {t("Subtipo")} <span className="required">*</span>
                  </label>
                  <select
                    className="package-logbook-form__select"
                    value={selectedSubtype}
                    onChange={(e) => setSelectedSubtype(e.target.value)}
                    required
                  >
                    <option value="">{t("Selecciona un subtipo")}</option>
                    {availableSubtypes.map(([key, label]) => (
                      <option key={key} value={key}>
                        {t(label)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Comentario */}
              <div className="package-logbook-form__field">
                <label className="package-logbook-form__label">
                  {t("Comentario")} <span className="required">*</span>
                </label>
                <textarea
                  className="package-logbook-form__textarea"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t("Escribe tu comentario aquí...")}
                  rows={4}
                  required
                />
              </div>

              {/* Botón enviar */}
              <button
                type="submit"
                className="package-logbook-form__submit"
                disabled={submitting || !selectedType || !selectedSubtype || !comment.trim()}
              >
                {submitting ? (
                  <>
                    <Spinner />
                    <span>{t("Guardando...")}</span>
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    <span>{t("Guardar bitácora")}</span>
                  </>
                )}
              </button>
            </div>
          </form>
          )}

          {/* Formulario para nueva incidencia. Sigue montado (sin renderizar
              nada) mientras se ve la otra pestaña: así ir a "Nueva bitácora" y
              volver no borra lo escrito, como cuando el estado vivía acá. */}
          <IncidentForm
            packageId={packageId}
            origin={incidentOrigin}
            hidden={activeTab !== "incident"}
            onCreated={handleIncidentCreated}
          />

          {/* Historial de bitácoras e incidencias */}
          <div className="package-logbook-history">
            <h3 className="package-logbook-history__title">{t("Historial")}</h3>
            
            {loading ? (
              <div className="package-logbook-history__skeleton">
                {[1, 2, 3].map((index) => (
                  <div key={index} className="package-logbook-skeleton-item">
                    <div className="package-logbook-skeleton-item__header">
                      <div className="package-logbook-skeleton-item__badges">
                        <div className="package-logbook-skeleton-item__badge package-logbook-skeleton-item__badge--type"></div>
                        <div className="package-logbook-skeleton-item__badge package-logbook-skeleton-item__badge--subtype"></div>
                      </div>
                      <div className="package-logbook-skeleton-item__meta">
                        <div className="package-logbook-skeleton-item__meta-item"></div>
                        <div className="package-logbook-skeleton-item__meta-item"></div>
                      </div>
                    </div>
                    <div className="package-logbook-skeleton-item__content">
                      <div className="package-logbook-skeleton-item__line"></div>
                      <div className="package-logbook-skeleton-item__line package-logbook-skeleton-item__line--short"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : logs.length === 0 && incidents.length === 0 ? (
              <div className="package-logbook-history__empty">
                <FileText size={48} />
                <p>{t("No hay registros")}</p>
                <span>{t("Las bitácoras e incidencias que agregues aparecerán aquí")}</span>
              </div>
            ) : (
              <div className="package-logbook-history__list">
                {/* Mostrar incidencias primero (ordenadas por prioridad) */}
                {sortedIncidents.map((incident) => {
                  const isExpanded = expandedIncidents.has(incident._id);
                  const descriptionLines = incident.description?.split('\n') || [];
                  const isLongDescription = descriptionLines.length > 2 || incident.description?.length > 150;
                  
                  return (
                    <div key={incident._id} className="package-logbook-item package-logbook-item--incident">
                      <div className="package-logbook-item__header">
                        <div className="package-logbook-item__badges">
                          <span className="package-logbook-item__type-badge package-logbook-item__type-badge--incident">
                            <AlertTriangle size={14} />
                            {t("Incidencia")}
                          </span>
                          <span className="package-logbook-item__subtype-badge">
                            {t(INCIDENT_SEVERITY[incident.severity] || incident.severity)}
                          </span>
                          <span className="package-logbook-item__subtype-badge">
                            {t(INCIDENT_TYPE_LABEL[incident.incidentType] || incident.incidentType || incident.type)}
                          </span>
                          {(() => {
                            const status = incident.status || (incident.resolvedAt ? "cerrada" : "abierta");
                            return (
                              <span className={`package-logbook-item__subtype-badge package-logbook-item__subtype-badge--${status === "cerrada" ? "resolved" : ""}`}
                                style={{ background: INCIDENT_STATUS_COLOR[status] === "success" ? "#d1e7dd" : INCIDENT_STATUS_COLOR[status] === "warning" ? "#fff3cd" : "#f8d7da", color: INCIDENT_STATUS_COLOR[status] === "success" ? "#0f5132" : INCIDENT_STATUS_COLOR[status] === "warning" ? "#664d03" : "#842029" }}>
                                {t(INCIDENT_STATUS[status] || status)}
                              </span>
                            );
                          })()}
                        </div>
                        <div className="package-logbook-item__meta">
                          <div className="package-logbook-item__user">
                            <User size={14} />
                            <span>
                              {incident.createdBy?.name 
                                ? `${incident.createdBy.name}${incident.createdBy.lastName ? ` ${incident.createdBy.lastName}` : ''}`
                                : t("Usuario desconocido")}
                            </span>
                          </div>
                          <div className="package-logbook-item__date">
                            <Clock size={14} />
                            <span>{moment(incident.createdAt).format("DD/MM/YYYY HH:mm")}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="package-logbook-item__content">
                        <div className="package-logbook-item__incident-details">
                          <p><strong>{t("Responsable:")}</strong> {t(INCIDENT_RESPONSIBLE[incident.responsible] || incident.responsible)}</p>
                          {incident.resolvedAt && (
                            <p><strong>{t("Resuelta el:")}</strong> {moment(incident.resolvedAt).format("DD/MM/YYYY HH:mm")}</p>
                          )}
                        </div>
                        {isLongDescription && !isExpanded ? (
                          <>
                            <p className="package-logbook-item__comment">
                              {incident.description.substring(0, 150)}...
                            </p>
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                              <button
                                className="package-logbook-item__expand"
                                onClick={() => toggleIncidentExpansion(incident._id)}
                              >
                                <ChevronDown size={16} />
                                <span>{t("Ver más")}</span>
                              </button>
                              <button
                                className="package-logbook-item__expand package-logbook-item__expand--view"
                                onClick={() => handleViewIncident(incident._id)}
                              >
                                <FileText size={16} />
                                <span>{t("Ver detalles")}</span>
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            {isLongDescription && (
                              <button
                                className="package-logbook-item__expand"
                                onClick={() => toggleIncidentExpansion(incident._id)}
                              >
                                <ChevronUp size={16} />
                                <span>{t("Ver menos")}</span>
                              </button>
                            )}
                            <p className="package-logbook-item__comment">
                              {incident.description}
                            </p>
                            <button
                              className="package-logbook-item__expand package-logbook-item__expand--view"
                              onClick={() => handleViewIncident(incident._id)}
                              style={{ marginTop: '0.5rem' }}
                            >
                              <FileText size={16} />
                              <span>{t("Ver detalles")}</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {/* Mostrar bitácoras después de las incidencias */}
                {logs.map((log) => {
                  const isExpanded = expandedLogs.has(log._id);
                  const commentLines = log.comment?.split('\n') || [];
                  const isLongComment = commentLines.length > 2 || log.comment?.length > 150;
                  
                  return (
                    <div key={log._id} className="package-logbook-item">
                      <div className="package-logbook-item__header">
                        <div className="package-logbook-item__badges">
                          <span className="package-logbook-item__type-badge">
                            {t(getTypeLabel(log.type))}
                          </span>
                          <span className="package-logbook-item__subtype-badge">
                            {t(getSubtypeLabel(log.type, log.subtype))}
                          </span>
                        </div>
                        <div className="package-logbook-item__meta">
                          <div className="package-logbook-item__user">
                            <User size={14} />
                            <span>
                              {log.createdBy?.name 
                                ? `${log.createdBy.name}${log.createdBy.lastName ? ` ${log.createdBy.lastName}` : ''}`
                                : t("Usuario desconocido")}
                            </span>
                          </div>
                          <div className="package-logbook-item__date">
                            <Clock size={14} />
                            <span>{moment(log.createdAt).format("DD/MM/YYYY HH:mm")}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="package-logbook-item__content">
                        {isLongComment && !isExpanded ? (
                          <>
                            <p className="package-logbook-item__comment">
                              {log.comment.substring(0, 150)}...
                            </p>
                            <button
                              className="package-logbook-item__expand"
                              onClick={() => toggleLogExpansion(log._id)}
                            >
                              <ChevronDown size={16} />
                              <span>{t("Ver más")}</span>
                            </button>
                          </>
                        ) : (
                          <>
                            {isLongComment && (
                              <button
                                className="package-logbook-item__expand"
                                onClick={() => toggleLogExpansion(log._id)}
                              >
                                <ChevronUp size={16} />
                                <span>{t("Ver menos")}</span>
                              </button>
                            )}
                            <p className="package-logbook-item__comment">
                              {log.comment}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de detalle de incidencia (nuevo) */}
      {selectedIncidentId && (
        <IncidentDetailModal
          incidentId={selectedIncidentId}
          onClose={handleIncidentModalClose}
          onUpdated={() => loadIncidents()}
        />
      )}

    </>
  );
}

